import { isNativeApp } from './platform.js';

const DB_NAME = 'rk-offline-papers';
const DB_VERSION = 1;
const STORE = 'pdfs';
const MAX_DAILY = 10;
const MAX_MONTHLY = 6;

function storeKey(kind, id) {
  return `${kind}:${id}`;
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'key' });
        os.createIndex('kind', 'kind', { unique: false });
        os.createIndex('savedAt', 'savedAt', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('IndexedDB aborted'));
  });
}

export async function saveOfflinePdf(kind, id, buffer) {
  if (!isNativeApp() || !kind || !id || !buffer) return false;
  try {
    const db = await openDb();
    const bytes =
      buffer instanceof ArrayBuffer
        ? buffer.slice(0)
        : buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength);
    const record = {
      key: storeKey(kind, id),
      kind,
      id,
      bytes,
      savedAt: Date.now(),
    };
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(record);
    await txDone(tx);
    await pruneOfflinePdfs(kind, kind === 'daily' ? MAX_DAILY : MAX_MONTHLY);
    return true;
  } catch {
    return false;
  }
}

export async function loadOfflinePdf(kind, id) {
  if (!isNativeApp() || !kind || !id) return null;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(storeKey(kind, id));
    const row = await new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    if (!row?.bytes) return null;
    return row.bytes instanceof ArrayBuffer ? row.bytes.slice(0) : row.bytes;
  } catch {
    return null;
  }
}

export async function hasOfflinePdf(kind, id) {
  const buf = await loadOfflinePdf(kind, id);
  return !!buf;
}

export async function pruneOfflinePdfs(kind, keep = MAX_DAILY) {
  if (!isNativeApp() || !kind) return;
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const idx = store.index('kind');
    const req = idx.getAll(kind);
    const rows = await new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    rows.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0));
    rows.slice(keep).forEach((row) => store.delete(row.key));
    await txDone(tx);
  } catch {
    /* ignore */
  }
}

export async function listOfflineIds(kind) {
  if (!isNativeApp() || !kind) return [];
  try {
    const db = await openDb();
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).index('kind').getAll(kind);
    const rows = await new Promise((resolve, reject) => {
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    return rows.map((r) => r.id).filter(Boolean);
  } catch {
    return [];
  }
}

export function offlineBlobUrl(buffer) {
  const copy = buffer instanceof ArrayBuffer ? buffer.slice(0) : buffer;
  const blob = new Blob([copy], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}
